import registryJson from "../../resources/journal-profiles/journal_route_registry.json" with { type: "json" };
import overridesJson from "../../resources/journal-profiles/high_frequency_journals.json" with { type: "json" };

type JournalProfile = {
  id: string;
  display_name: string;
  profile_file: string;
  template_family: string;
  template_folder: string;
  default_override_file: string;
  aliases: string[];
};

type JournalOverride = {
  id: string;
  profile_id: string;
  override_file: string;
  journal_names: string[];
  aliases: string[];
  official_sources_to_check: string[];
  known_likely_requirements: string[];
  stage5_override_points: string[];
  must_not_assume: string[];
};

type JournalRegistry = {
  version: string;
  unknown_route: {
    profile: string;
    action: string;
  };
  profiles: JournalProfile[];
};

type JournalOverrideIndex = {
  version: string;
  entries: JournalOverride[];
};

export type JournalMatchSource = "high-frequency" | "publisher-profile" | "none";

export type JournalProfileMatch = {
  input: string;
  matched: boolean;
  profileId: string;
  displayName: string;
  profileFile: string;
  sourceProfileFile: string;
  overrideId: string;
  overrideFile: string;
  sourceOverrideFile: string;
  templateFamily: string;
  matchedAlias: string;
  matchSource: JournalMatchSource;
  registryVersion: string;
  verificationStatus: "bundled-reference-not-reverified";
  officialSourcesToCheck: string[];
  knownLikelyRequirements: string[];
  stage5OverridePoints: string[];
  mustNotAssume: string[];
  nextActions: string[];
};

const registry = registryJson as JournalRegistry;
const overrideIndex = overridesJson as JournalOverrideIndex;
const FAMILY_PREFIX_ALIASES = new Set([
  "ieee transactions",
  "lancet regional health",
  "expert review of",
  "expert opinion",
  "materials today",
]);

function filename(path: string): string {
  return path.replaceAll("\\", "/").split("/").at(-1) ?? path;
}

export function normalizeJournalName(value: string): string {
  return value
    .toLowerCase()
    .replaceAll("&", " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function aliasMatchScore(
  query: string,
  alias: string,
  allowFamilyPrefix = false,
): number {
  const normalizedAlias = normalizeJournalName(alias);
  if (!normalizedAlias) return 0;
  if (query === normalizedAlias) return 1_000_000 + normalizedAlias.length;
  // The Skill's command-line router also accepts arbitrary bidirectional
  // substrings. That is unsafe in a live input: "Bio", "ONE" and "Journal"
  // can otherwise route to unrelated publishers while the user is typing.
  // Only an explicitly whitelisted journal-family prefix may match a longer
  // name. Ordinary aliases such as "biology", "sensors" and "materials"
  // must remain exact or they can steal real journals from other publishers.
  if (
    allowFamilyPrefix &&
    query.startsWith(`${normalizedAlias} `)
  ) {
    return 100_000 + normalizedAlias.length;
  }
  return 0;
}

function findProfile(profileId: string): JournalProfile | undefined {
  return registry.profiles.find((profile) => profile.id === profileId);
}

function defaultOverrideFor(profile: JournalProfile): JournalOverride | undefined {
  return overrideIndex.entries.find(
    (entry) =>
      entry.profile_id === profile.id &&
      entry.override_file === profile.default_override_file,
  );
}

function matchOverride(
  query: string,
): { score: number; entry: JournalOverride; alias: string } | undefined {
  let best: { score: number; entry: JournalOverride; alias: string } | undefined;
  for (const entry of overrideIndex.entries) {
    for (const alias of [...entry.journal_names, ...entry.aliases]) {
      const score = aliasMatchScore(query, alias);
      if (score && (!best || score > best.score)) best = { score, entry, alias };
    }
  }
  return best;
}

function matchProfile(
  query: string,
): { score: number; profile: JournalProfile; alias: string } | undefined {
  let best:
    | { score: number; profile: JournalProfile; alias: string }
    | undefined;
  for (const profile of registry.profiles) {
    for (const alias of [profile.display_name, profile.id, ...profile.aliases]) {
      const score = aliasMatchScore(
        query,
        alias,
        FAMILY_PREFIX_ALIASES.has(normalizeJournalName(alias)),
      );
      if (score && (!best || score > best.score)) best = { score, profile, alias };
    }
  }
  return best;
}

function formatKnownMatch(
  input: string,
  profile: JournalProfile,
  alias: string,
  source: Exclude<JournalMatchSource, "none">,
  exactOverride?: JournalOverride,
): JournalProfileMatch {
  // A publisher-only input (for example, "Elsevier") still needs a useful
  // checklist. In that case we show the profile's default checklist, but keep
  // the source label distinct from an exact high-frequency journal match.
  const checklist = exactOverride ?? defaultOverrideFor(profile);
  const sourceOverrideFile = checklist?.override_file ?? profile.default_override_file;
  return {
    input,
    matched: true,
    profileId: profile.id,
    displayName: profile.display_name,
    profileFile: `resources/journal-profiles/profiles/${filename(profile.profile_file)}`,
    sourceProfileFile: profile.profile_file,
    overrideId: checklist?.id ?? "",
    overrideFile: sourceOverrideFile
      ? `resources/journal-profiles/overrides/${filename(sourceOverrideFile)}`
      : "",
    sourceOverrideFile,
    templateFamily: profile.template_family,
    matchedAlias: alias,
    matchSource: source,
    registryVersion: registry.version,
    verificationStatus: "bundled-reference-not-reverified",
    officialSourcesToCheck: checklist?.official_sources_to_check ?? [],
    knownLikelyRequirements: checklist?.known_likely_requirements ?? [],
    stage5OverridePoints: checklist?.stage5_override_points ?? [],
    mustNotAssume: checklist?.must_not_assume ?? [],
    nextActions: [
      "确认准确期刊名称和文章类型",
      "核对期刊官网 Instructions for Authors 与当前 Word/LaTeX 模板",
      "记录官网链接、访问日期、参考文献样式和声明要求",
      "官网核验完成后再把差异规则应用到最终 Word",
    ],
  };
}

export function matchJournalProfile(name: string): JournalProfileMatch {
  const input = name.trim();
  const query = normalizeJournalName(input);
  if (query) {
    const override = matchOverride(query);
    if (override) {
      const profile = findProfile(override.entry.profile_id);
      if (profile) {
        return formatKnownMatch(
          input,
          profile,
          override.alias,
          "high-frequency",
          override.entry,
        );
      }
    }

    const profile = matchProfile(query);
    if (profile) {
      return formatKnownMatch(
        input,
        profile.profile,
        profile.alias,
        "publisher-profile",
      );
    }
  }

  return {
    input,
    matched: false,
    profileId: registry.unknown_route.profile || "generic_unknown",
    displayName: "",
    profileFile: "",
    sourceProfileFile: "",
    overrideId: "",
    overrideFile: "",
    sourceOverrideFile: "",
    templateFamily: "",
    matchedAlias: "",
    matchSource: "none",
    registryVersion: registry.version,
    verificationStatus: "bundled-reference-not-reverified",
    officialSourcesToCheck: [],
    knownLikelyRequirements: [],
    stage5OverridePoints: [],
    mustNotAssume: [],
    nextActions: [
      registry.unknown_route.action ||
        "检索目标期刊官网的 Instructions for Authors 与模板页面",
    ],
  };
}

export function getJournalProfileCapabilities() {
  return {
    registryVersion: registry.version,
    profileCount: registry.profiles.length,
    highFrequencyChecklistCount: overrideIndex.entries.length,
    matchingOrder: ["high-frequency-journal", "publisher-profile", "unknown"],
    verificationRequiredBeforeFinalFormatting: true,
  } as const;
}

export function getJournalSuggestions(): string[] {
  const names = [
    ...registry.profiles.map((profile) => profile.display_name),
    ...overrideIndex.entries.flatMap((entry) => entry.journal_names),
  ];
  return [...new Set(names)].sort((left, right) => left.localeCompare(right));
}
